import { createContext, useContext, useMemo, useRef, useSyncExternalStore } from 'react'
import type { ReactNode, RefCallback } from 'react'
import type { TourAnchorId } from '@/lib/productTour'

/**
 * The registry of live DOM nodes the founder product tour can point at —
 * ClickUp 86d3uknz0.
 *
 * Nav chrome hands the tour its nodes through a ref rather than the tour going
 * looking for them. That is not only tidier than a `querySelector` sweep, it is
 * the difference between a correct tour and a stale one: rail sections come and
 * go with feature flags, and a registry that empties when an icon unmounts
 * re-renders the tour with the right answer, where a selector read at
 * stop-change time would still be holding the detached node.
 *
 * Anchors are addressed by the stable ids in `TOUR_ANCHORS`, never by class
 * name — CSS-module classes are hashed per build, so a tour keyed on them
 * breaks silently on an unrelated styling edit.
 *
 * **An external store, not component state.** Registering must not re-render
 * the component that registered: a ref callback that re-renders its own owner
 * is one unstable `ref={(node) => …}` away from an infinite attach/detach loop,
 * and nav chrome is exactly the place where someone will eventually write one.
 * Only `useTourAnchorNodes` subscribes, so the rail and the sidebar pay nothing
 * for anchors they merely host.
 *
 * Outside a provider (every shell that is not the authenticated one, and any
 * component rendered on its own in a test) `register` returns undefined, which
 * React accepts as "no ref", and the map stays empty. Registering is therefore
 * always safe to call, and no component needs to know whether a tour exists.
 */

interface TourAnchorStore {
  /**
   * A ref callback for this anchor, stable across renders so React does not
   * detach and reattach the node on every render of its owner. Undefined for an
   * undefined id, so call sites can pass an optional anchor straight through.
   */
  register: (anchorId: TourAnchorId | undefined) => RefCallback<HTMLElement> | undefined
  subscribe: (onChange: () => void) => () => void
  /** Cached between changes — `useSyncExternalStore` requires a stable identity. */
  getSnapshot: () => ReadonlyMap<TourAnchorId, HTMLElement>
}

const EMPTY_ANCHORS: ReadonlyMap<TourAnchorId, HTMLElement> = new Map()

const INERT_STORE: TourAnchorStore = {
  register: () => undefined,
  subscribe: () => () => {},
  getSnapshot: () => EMPTY_ANCHORS,
}

const TourAnchorContext = createContext<TourAnchorStore>(INERT_STORE)

function createTourAnchorStore(): TourAnchorStore {
  const nodes = new Map<TourAnchorId, HTMLElement>()
  const callbacks = new Map<TourAnchorId, RefCallback<HTMLElement>>()
  const listeners = new Set<() => void>()
  let snapshot: ReadonlyMap<TourAnchorId, HTMLElement> = EMPTY_ANCHORS

  const publish = () => {
    snapshot = new Map(nodes)
    for (const listener of listeners) listener()
  }

  return {
    register(anchorId) {
      if (!anchorId) return undefined
      const existing = callbacks.get(anchorId)
      if (existing) return existing

      const callback: RefCallback<HTMLElement> = (node) => {
        // React 19 hands a node here and a cleanup below, never null — but the
        // ref type still admits it, and a null in the map would be a candidate
        // the tour thinks it can frame.
        if (!node) return
        nodes.set(anchorId, node)
        publish()
        // React 19 ref cleanup. With one returned, React never calls this
        // callback with null — the cleanup is the whole unmount signal.
        return () => {
          // Guard against a re-registration having already claimed the id: on a
          // move, React attaches the new node before detaching the old one.
          if (nodes.get(anchorId) !== node) return
          nodes.delete(anchorId)
          publish()
        }
      }
      callbacks.set(anchorId, callback)
      return callback
    },
    subscribe(onChange) {
      listeners.add(onChange)
      return () => listeners.delete(onChange)
    },
    getSnapshot: () => snapshot,
  }
}

export function TourAnchorProvider({ children }: { children: ReactNode }) {
  const store = useRef<TourAnchorStore>(null)
  store.current ??= createTourAnchorStore()
  return <TourAnchorContext.Provider value={store.current}>{children}</TourAnchorContext.Provider>
}

/**
 * The registration side of the registry. Safe to call anywhere — outside a
 * provider it hands back a `register` that returns undefined.
 */
export function useTourAnchors(): Pick<TourAnchorStore, 'register'> {
  return useContext(TourAnchorContext)
}

/**
 * The mounted nodes for an ordered list of candidate anchors, in that order.
 *
 * Ordering is the fallback chain: a stop's own targets first, then whatever
 * always exists. Absent anchors simply drop out, so the caller receives exactly
 * the candidates that are real — which is what keeps the tour from ever framing
 * a node that is not there.
 */
export function useTourAnchorNodes(anchorIds: readonly TourAnchorId[]): HTMLElement[] {
  const { subscribe, getSnapshot } = useContext(TourAnchorContext)
  const anchors = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  return useMemo(
    () => anchorIds.map((id) => anchors.get(id)).filter((node): node is HTMLElement => !!node),
    [anchorIds, anchors],
  )
}
